import { BUDDY_CONFIG } from 'config/buddy/config';
import klaxMascotVideoLight from 'assets/images/magnific_mascott-t-light.mp4';
import klaxMascotVideoDark from 'assets/images/magnific_mascott-t.mp4';
import { useTheme } from 'components/core/theme-provider';
import { MOBILE_BREAKPOINT } from 'hooks/use-mobile';
import { cn } from 'lib/utils';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

/** Mascot box. size-12 is 48px, the scale step nearest 50. */
const MASCOT_SIZE = 48;
/** Figma 1327:11351 — compact row gap */
const COMPACT_GAP_PX = 10;
/** Stacked gap between buddy and text. */
const STACKED_GAP_CLASS = 'gap-4.5';
/** Figma 2932:27983 — stacked text width */
const STACKED_TEXT_MAX_WIDTH_PX = 600;
const STACKED_TEXT_WIDTH_CLASS = 'max-w-[600px]';

export interface ChatGreetingProps {
  className?: string;
  title: string;
  subtitle?: string;
}

function ChatGreetingMascot() {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const klaxMascotVideo = isDark ? klaxMascotVideoDark : klaxMascotVideoLight;
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.currentTime = 0;

    void video.play().catch(() => {
      // Autoplay may be blocked; keep the first frame visible.
    });
  }, [klaxMascotVideo]);

  const handleVideoEnded = () => {
    const video = videoRef.current;
    if (!video) return;

    video.currentTime = 0;
    video.pause();
  };

  return (
    <div
      className="size-12 shrink-0 overflow-hidden rounded-full"
      style={{ width: MASCOT_SIZE, height: MASCOT_SIZE }}
    >
      <video
        ref={videoRef}
        key={resolvedTheme}
        src={klaxMascotVideo}
        muted
        playsInline
        preload="auto"
        aria-hidden
        onEnded={handleVideoEnded}
        className={cn(
          'size-full object-cover',
          isDark ? 'scale-[1.30] mix-blend-screen' : 'scale-[1.30]'
        )}
        width={MASCOT_SIZE}
        height={MASCOT_SIZE}
      />
    </div>
  );
}

export function ChatGreeting({ className, title, subtitle }: ChatGreetingProps) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const [isStackedLayout, setIsStackedLayout] = useState(false);

  const titleClassName = cn(
    'font-heyJune text-greeting font-normal 2xl:text-greeting-lg ',
    isDark ? 'text-greeting-muted' : 'text-grey-950'
  );
  const subtitleClassName = cn(
    'text-center text-sm font-medium leading-display tracking-tight 2xl:text-base',
    isDark ? 'text-grey-300' : 'text-grey-500'
  );

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateLayout = () => {
      const measure = measureRef.current;
      if (!measure) return;

      const containerWidth = container.clientWidth;
      if (containerWidth === 0) return;

      const textWidth = measure.offsetWidth;
      const availableCompactWidth = containerWidth - MASCOT_SIZE - COMPACT_GAP_PX;

      // Mobile: always stack mascot above text (same as loading-overlay).
      setIsStackedLayout(
        containerWidth < MOBILE_BREAKPOINT ||
          textWidth > STACKED_TEXT_MAX_WIDTH_PX ||
          textWidth > availableCompactWidth
      );
    };

    updateLayout();

    const resizeObserver = new ResizeObserver(updateLayout);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, [title]);

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative flex w-full',
        isStackedLayout
          ? cn('flex-col items-center', STACKED_GAP_CLASS)
          : 'items-center justify-center gap-2.5',
        className
      )}
    >
      <span
        ref={measureRef}
        aria-hidden
        className={cn(
          'pointer-events-none invisible absolute left-0 top-0 -z-10 whitespace-nowrap',
          titleClassName
        )}
      >
        {title}
      </span>

      {/* The floating companion replaces this mascot when Buddy is on. */}
      {BUDDY_CONFIG.on ? null : <ChatGreetingMascot />}

      <div
        className={cn(
          'flex min-w-0 flex-col items-center gap-2',
          isStackedLayout && cn('w-full', STACKED_TEXT_WIDTH_CLASS)
        )}
      >
        <p
          className={cn(
            titleClassName,
            'text-center',
            isStackedLayout ? 'w-full min-w-0 break-words' : 'whitespace-nowrap'
          )}
        >
          {title}
        </p>
        {subtitle ? <p className={subtitleClassName}>{subtitle}</p> : null}
      </div>
    </div>
  );
}
